import {
  evaluateExchangeOption,
  variantId,
  variantPrice,
} from "./exchangePolicy.js";

export const SEARCH_DEBOUNCE_MS = 280;
export const MIN_SEARCH_LENGTH = 2;
export const SEARCH_PAGE_SIZE = 8;
export const PREVIEW_SEARCH_PAGE_SIZE = 2;

export function emptyPageInfo() {
  return {
    hasNextPage: false,
    hasPreviousPage: false,
    startCursor: null,
    endCursor: null,
  };
}

export function paginatePreviewProducts(products, { after, first = PREVIEW_SEARCH_PAGE_SIZE } = {}) {
  const list = Array.isArray(products) ? products : [];
  const start = after ? Number(after) || 0 : 0;
  const pageSize = Math.max(1, Number(first) || PREVIEW_SEARCH_PAGE_SIZE);
  const page = list.slice(start, start + pageSize);
  const next = start + page.length;
  return {
    products: page,
    pageInfo: {
      hasNextPage: next < list.length,
      hasPreviousPage: start > 0,
      startCursor: start > 0 ? String(start) : null,
      endCursor: next < list.length ? String(next) : null,
    },
  };
}

export function normalizeProductPage(payload) {
  if (Array.isArray(payload)) {
    return { products: payload, pageInfo: emptyPageInfo() };
  }
  return {
    products: payload?.products || [],
    pageInfo: {
      ...emptyPageInfo(),
      ...(payload?.pageInfo || {}),
    },
  };
}

export function normalizeSearchQuery(value) {
  return String(value || "").trim();
}

export function shouldSearch(query) {
  return normalizeSearchQuery(query).length >= MIN_SEARCH_LENGTH;
}

export function isInStock(variant) {
  const quantity = variant?.inventoryQuantity ?? variant?.inventory_quantity;
  if (quantity === undefined || quantity === null || quantity === "") {
    return true;
  }
  return Number(quantity) > 0;
}

export function stockAvailabilityLabel(variant) {
  const quantity = variant?.inventoryQuantity ?? variant?.inventory_quantity;
  if (quantity === undefined || quantity === null || quantity === "") {
    return "";
  }
  return Number(quantity) > 0 ? "Em estoque" : "Esgotado";
}

export function sameProductVariants(item) {
  return (item?.availableVariants || [])
    .map((variant) => ({
      ...variant,
      product_id: variant.product_id || item.product_id,
      productTitle: variant.productTitle || item.title,
      image: variant.image || item.image,
    }))
    .filter((variant) => {
      const itemProduct = String(item?.product_id || item?.productId || "");
      const variantProduct = String(variant.product_id || variant.productId || itemProduct);
      return !itemProduct || !variantProduct || itemProduct === variantProduct;
    });
}

export function productOptions(product) {
  return (product?.options || []).filter((option) => (
    option?.name
    && option.name !== "Title"
    && Array.isArray(option.values)
    && option.values.length > 0
  ));
}

export function variantOptionMap(variant, optionNames = []) {
  if (variant?.selectedOptions?.length) {
    return Object.fromEntries(
      variant.selectedOptions
        .filter((option) => option?.name)
        .map((option) => [option.name, option.value]),
    );
  }
  const parts = String(variant?.title || "").split(" / ").map((part) => part.trim()).filter(Boolean);
  return optionNames.reduce((acc, name, index) => {
    if (parts[index]) {
      acc[name] = parts[index];
    }
    return acc;
  }, {});
}

export function findVariantByOptions(variants, selected, optionNames) {
  const names = optionNames || Object.keys(selected || {});
  const required = names.filter((name) => selected?.[name]);
  if (!required.length) {
    return null;
  }
  return (variants || []).find((variant) => {
    const map = variantOptionMap(variant, names);
    return required.every((name) => map[name] === selected[name]);
  }) || null;
}

export function optionValueState(variants, optionName, value, selected, optionNames) {
  const next = { ...selected, [optionName]: value };
  const matches = (variants || []).filter((variant) => {
    const map = variantOptionMap(variant, optionNames);
    return optionNames.every((name) => !next[name] || map[name] === next[name]);
  });
  if (!matches.length) {
    return "unavailable";
  }
  if (matches.some((variant) => isInStock(variant))) {
    return "available";
  }
  return "oos";
}

export function annotateVariants(item, variants, policy) {
  const seen = new Set();
  return (variants || [])
    .filter((variant) => {
      const id = String(variantId(variant));
      if (!id || seen.has(id)) {
        return false;
      }
      seen.add(id);
      return true;
    })
    .map((variant) => {
      const evaluated = evaluateExchangeOption(item, variant, policy);
      const inStock = isInStock(variant);
      if (evaluated.allowed && !inStock) {
        return {
          variant,
          allowed: false,
          reason: "Sem estoque",
          dueAmount: evaluated.dueAmount,
          inStock: false,
        };
      }
      return {
        variant,
        ...evaluated,
        inStock,
      };
    });
}

export function catalogProductSummary(product, item, policy) {
  const annotated = annotateVariants(item, product?.variants || [], policy);
  const selectable = annotated.filter((option) => option.allowed && option.inStock);
  const priced = (selectable.length ? selectable : annotated.filter((option) => option.inStock))
    .map((option) => variantPrice(option.variant, 0))
    .filter((price) => Number.isFinite(price));
  return {
    available: selectable.length > 0,
    inStockCount: annotated.filter((option) => option.inStock).length,
    minPrice: priced.length ? Math.min(...priced) : null,
    maxPrice: priced.length ? Math.max(...priced) : null,
  };
}

export function matchesPreviewSearch(product, query) {
  const needle = normalizeSearchQuery(query).toLowerCase();
  if (!needle) {
    return false;
  }
  const title = String(product?.title || product?.name || "").toLowerCase();
  const skus = (product?.variants || []).map((variant) => String(variant.sku || "").toLowerCase());
  return title.includes(needle) || skus.some((sku) => sku.includes(needle));
}

export function formatBRL(price) {
  return Number(price || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function formatPriceRange(minPrice, maxPrice, formatPrice = formatBRL) {
  if (minPrice == null) {
    return "";
  }
  const min = formatPrice(minPrice);
  if (maxPrice == null || Math.abs(maxPrice - minPrice) < 0.009) {
    return `R$ ${min}`;
  }
  return `R$ ${min} – R$ ${formatPrice(maxPrice)}`;
}
